import {
  Avatar,
  Box,
  Button,
  Card,
  CheckboxInput,
  FormControl,
  Icon,
  Input,
  MessageBlock,
  MinusIcon,
  NavList,
  PlusIcon,
  Switch,
  UserIcon,
} from '@deckee/deck-hand';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { captureEvent } from '../../../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../../../services/feature';
import {
  getStoredImage,
  getWatercraftImage,
} from '../../../../../../services/image';
import {
  getWatercraftString,
  isVesselFloatPlanReady,
} from '../../../../../../services/log-trips/float-plan';
import { setShowingMenuBar } from '../../../../../../services/store/modules/app-controls';
import { selectCurrentMember } from '../../../../../../services/store/modules/backend-service';
import {
  reset as resetOldFloatplan,
  setEndTime,
  setLoloEnabled,
  setOwner,
  setPeopleOnBoard,
  setStartTime,
  setTimeType,
} from '../../../../../../services/store/modules/float-plan';
import {
  fetchExistingEmergencyContacts,
  reset,
  selectAllEmergencyContacts,
  selectSelectedEmergencyContacts,
  setPlanHasDiving,
  setPlanIsLoggingOn,
} from '../../../../../../services/store/modules/plan';
import { isValidLoggedOnVessel } from '../../../../../../services/store/modules/vessels';
import useIsInBounds from '../../../../../hooks/use-is-in-bounds';
import BasicDrawerLayout from '../../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../../../layouts/components/LayoutBody/LayoutBody';
import IconWithTextLockup from '../../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import BlueCircleWrapper from '../../../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import AnchorButton from '../../../../../shared/AnchorButton/AnchorButton';
import CheckSwitch from '../../../../../shared/_deprecated/components/CheckSwitch/CheckSwitch';
import useVessels from '../../../../Vessels/hooks/use-vessels';

const StyledDateInput = styled(Input)`
  :out-of-range {
    border: 2px solid ${({ theme }) => theme.colors.danger};
  }
`;

const CreateFloatPlanStep = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const context = useSelector((state) => state.floatPlan);
  const isLoggingOn = useSelector((state) => state.planDetails.planIsLoggingOn);
  const isDivingState = useSelector((state) => state.planDetails.isDiving);
  const member = useSelector(selectCurrentMember);
  const isInNsw = useIsInBounds('NEWSOUTHWALES');
  const isInWa = useIsInBounds('LOLO_TRIAL');
  const theme = useTheme();

  const { selectedVessel: vessel, isSelectedLoggedOnVesselInvalid } =
    useVessels();

  const [isOneWay, setOneWay] = React.useState(context.time_type === 'arrival');
  const [onboard, setOnboard] = React.useState(context.people_aboard);

  const selectedEmergencyContacts = useSelector(
    selectSelectedEmergencyContacts,
  );
  const allEmergencyContacts = useSelector(selectAllEmergencyContacts);

  // It would be ideal if we didn't have to access a property on the window to find out whether you're
  // in WA. This could be re-addressed at a later date.
  const isLoloUser =
    hasFeatureEnabled('log-on-log-off') &&
    (isInWa || hasFeatureEnabled('force-log-on-log-off'));

  const [logonWithWa, setLogonWithWa] = useState(
    isLoggingOn === undefined ? isLoloUser : isLoggingOn,
  );

  const [isDiving, setIsDiving] = useState(isDivingState);

  useEffect(() => {
    dispatch(fetchExistingEmergencyContacts());
  }, [dispatch]);

  useEffect(() => {
    captureEvent({
      type: 'float: marine rescue toggle state',
      payload: {
        currentState: logonWithWa,
      },
    });
  }, [logonWithWa]);

  // This adds an event listener that will scroll the active(focused) element into view
  // when the keyboard is displayed. This is needed for the people on board input.
  useEffect(() => {
    const scrollActiveElementInView = () => {
      document.activeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      });
    };
    window.addEventListener('keyboardDidShow', scrollActiveElementInView);

    return () => {
      window.removeEventListener('keyboardDidShow', scrollActiveElementInView);
    };
  }, []);

  const showMenuBar = (show = true) => {
    dispatch(setShowingMenuBar(show));
  };

  const onTimeChange = async (event) => {
    const newEndTime = event.target?.value;
    dispatch(setEndTime(newEndTime));
    await captureEvent({
      type: 'float: set arrival time',
      payload: {
        time: newEndTime,
      },
      sendToAmplitude: true,
    });
  };

  const getTimeType = (checked) => (checked ? 'arrival' : 'return');

  const onTimeTypeChange = async (timeType) => {
    setOneWay(timeType);
    dispatch(setTimeType(getTimeType(timeType)));
    await captureEvent({
      type: 'float: set trip type',
      payload: {
        tripType: getTimeType(timeType),
      },
      sendToAmplitude: true,
    });
  };

  const handlePeopleOnBoardChange = async (event) => {
    const { value } = event.target;
    await captureEvent({
      type: 'float: set people on board',
      payload: {
        number: value,
      },
      sendToAmplitude: true,
    });

    setOnboard(value);
  };

  const onMinusIconClick = async () => {
    const total = onboard - 1;

    if (total >= 1) {
      await captureEvent({
        type: 'float: set people on board',
        payload: {
          number: total,
        },
        sendToAmplitude: true,
      });
      setOnboard(total);
      dispatch(setPeopleOnBoard(total));
    }
  };

  const hasOnLandContact = () =>
    selectedEmergencyContacts.filter(
      (contact) => contact.contactType === 'onland',
    ).length > 0;

  const hasInvalidLoggedInContacts = () => {
    // Ensure that there's at least one contact on land
    const onLandContactPresent = hasOnLandContact();
    if (
      isLoggingOn &&
      (selectedEmergencyContacts.length === 0 || !onLandContactPresent)
    ) {
      return true;
    }

    return false;
  };

  const hasInvalidLoggedInOwner = () => {
    if (
      isLoggingOn &&
      (!member.phoneNumber ||
        !member.fullName ||
        !member.fullName.trim().length > 0)
    ) {
      return true;
    }

    return false;
  };

  const handleSwitchIsLoggingOn = () => {
    setLogonWithWa(!logonWithWa);
    dispatch(setPlanIsLoggingOn(!logonWithWa));
    dispatch(setLoloEnabled(!logonWithWa));

    captureEvent({
      type: 'float: marine rescue toggle interaction',
      payload: {
        switchTo: !logonWithWa,
      },
    });
  };

  const handleSwitchIsDiving = () => {
    setIsDiving(!isDiving);
  };

  useEffect(() => {
    dispatch(setPlanIsLoggingOn(logonWithWa));
    dispatch(setLoloEnabled(logonWithWa));
  }, [dispatch, logonWithWa]);

  useEffect(() => {
    dispatch(setPlanHasDiving(isDiving));
  }, [dispatch, isDiving]);

  const onPlusIconClick = async () => {
    const total = onboard + 1;
    await captureEvent({
      type: 'float: set people on board',
      payload: {
        number: total,
      },
      sendToAmplitude: true,
    });
    setOnboard(total);
    dispatch(setPeopleOnBoard(total));
  };

  const isLoggedInContactsValid = () => {
    // If logging on, check that contacts are valid
    if (isLoggingOn) {
      return (
        !hasInvalidLoggedInContacts() &&
        !hasInvalidLoggedInOwner() &&
        selectedEmergencyContacts.length !== 0
      );
    }
    return selectedEmergencyContacts.length !== 0;
  };

  const isStepComplete = () =>
    isVesselFloatPlanReady(vessel, isLoggingOn) &&
    !isSelectedLoggedOnVesselInvalid &&
    !hasInvalidLoggedInContacts() &&
    !hasInvalidLoggedInOwner() &&
    context.end_time != null &&
    DateTime.fromISO(context.end_time) > DateTime.now() &&
    selectedEmergencyContacts.length &&
    onboard >= 1;

  const resetFloatPlanStore = () => {
    dispatch(reset());
    dispatch(resetOldFloatplan());
  };

  const onComplete = async () => {
    dispatch(setOwner(member));
    dispatch(setTimeType(getTimeType(isOneWay)));
    dispatch(setPlanIsLoggingOn(logonWithWa));
    dispatch(setPlanHasDiving(isDiving));

    // only set the start time when it is a float-plan being created
    if (!context.id) {
      dispatch(setStartTime(DateTime.now()));
    }

    history.push('/maps/plan/details');
  };

  const handleOnBackAndClose = () => {
    if (context?.share_token) {
      history.push(`/maps/float-plan/${context.share_token}`);
    } else {
      resetFloatPlanStore();
      history.push('/maps');
    }
  };

  return (
    <BasicDrawerLayout
      onBack={() => handleOnBackAndClose()}
      onClose={() => handleOnBackAndClose()}
      heading="Your plan"
      showMenuBar={false}
    >
      <LayoutBody>
        <form>
          <Card>
            <Card.Body>
              <NavList edge>
                {/* WA log on toggle */}

                {isInNsw && !isLoloUser && (
                  <>
                    <NavList.Item
                      data-testid="lolo-log-on-switch"
                      onClick={() => {
                        // handleSwitchIsLoggingOn();
                      }}
                    >
                      <IconWithTextLockup
                        useEllipsis
                        flex="1 0 0px"
                        emphasis="description"
                        descriptionColor="black"
                        image={
                          <Avatar
                            src={getStoredImage(
                              '84488937d4494c0ec0af3d8b97c2f6f33c605b64fa0bb5084c91372b5d1387c4',
                              40,
                            )}
                          />
                        }
                        heading="Log on"
                        description="Marine Rescue NSW"
                      />
                    </NavList.Item>
                    <MessageBlock type="warning" m="2">
                      It is strongly advised that you log on via Channel 16 on
                      VHF, Channel 88 on 27MHz, or the Marine Rescue NSW app.
                    </MessageBlock>
                  </>
                )}

                {/* WA log on toggle */}
                {isLoloUser && (
                  <NavList.Item
                    data-testid="lolo-log-on-switch"
                    onClick={() => {
                      handleSwitchIsLoggingOn();
                    }}
                  >
                    <IconWithTextLockup
                      useEllipsis
                      flex="1 0 0px"
                      emphasis="description"
                      descriptionColor="black"
                      image={
                        <Avatar
                          src={getStoredImage(
                            '8a33b5efc6fb65fe0a60e220df4d1634ecbb84029254a97f73929b71435e58e9',
                            40,
                          )}
                        />
                      }
                      heading="Log on"
                      description="Marine Rescue WA"
                    />

                    <CheckSwitch isActive={logonWithWa} />
                  </NavList.Item>
                )}

                {!logonWithWa && isLoloUser && (
                  <MessageBlock type="warning" m="2">
                    It is strongly advised that you log on with Marine Rescue WA
                    to let them know your plans.
                  </MessageBlock>
                )}

                {/* Vessel */}

                <NavList.Item
                  chevron
                  onClick={() => {
                    history.push('/maps/vessels/select-vessel', {
                      returnUrl: history.location.pathname,
                    });
                  }}
                >
                  <IconWithTextLockup
                    useEllipsis
                    emphasis="description"
                    descriptionColor="black"
                    flex="1 0 0px"
                    image={
                      vessel?.image != null ? (
                        <Avatar src={getStoredImage(vessel?.image, 40)} />
                      ) : (
                        getWatercraftImage(vessel?.watercraft_type, 40)
                      )
                    }
                    heading="Watercraft"
                    description={
                      <>
                        {vessel == null || vessel?.id == null
                          ? 'Add watercraft'
                          : getWatercraftString(vessel)}
                        {(isLoggingOn && !isValidLoggedOnVessel(vessel)) ||
                        !isVesselFloatPlanReady(vessel, isLoggingOn) ? (
                          <Box pt="1" color="danger" fontSize="1">
                            More details needed
                          </Box>
                        ) : null}
                      </>
                    }
                  />
                </NavList.Item>

                <NavList.Item
                  chevron
                  onClick={() =>
                    history.push(
                      allEmergencyContacts.length === 0
                        ? '/maps/plan/emergency-contacts/add'
                        : '/maps/plan/emergency-contacts',
                    )
                  }
                >
                  <IconWithTextLockup
                    useEllipsis
                    heading="Emergency contacts"
                    emphasis="description"
                    descriptionColor={
                      selectedEmergencyContacts.length === 0 ||
                      !isLoggedInContactsValid()
                        ? 'danger'
                        : 'black'
                    }
                    description={
                      <>
                        {selectedEmergencyContacts.length === 0 && (
                          <Box fontSize="1">Add contacts</Box>
                        )}
                        {selectedEmergencyContacts.length > 0 &&
                          !isLoggedInContactsValid() && (
                            <Box fontSize="1">
                              {!hasOnLandContact()
                                ? 'At least one onland emergency contact must be provided'
                                : 'More details needed'}
                            </Box>
                          )}
                        {selectedEmergencyContacts.length > 0 &&
                          isLoggedInContactsValid() &&
                          [
                            {
                              name: member?.fullName
                                ? `${member.fullName} (you)`
                                : 'You',
                            },
                            ...selectedEmergencyContacts,
                          ]
                            .map(({ name }) => name)
                            .join(', ')}
                      </>
                    }
                    image={
                      <BlueCircleWrapper>
                        <UserIcon color="#001A70" />
                      </BlueCircleWrapper>
                    }
                  />
                </NavList.Item>

                {/* ETA */}
                <Switch.Group
                  name="time-type"
                  value={isOneWay ? 'oneway' : 'return'}
                  onChange={(e) => {
                    onTimeTypeChange(e.target.value === 'oneway');
                  }}
                >
                  <Switch id="return" value="return">
                    Return trip
                  </Switch>
                  <Switch id="oneway" value="oneway">
                    One way trip
                  </Switch>
                </Switch.Group>
                <Box mt="2">
                  <FormControl
                    required
                    name="finishDate"
                    label={isOneWay ? 'Time of arrival' : 'Time of return'}
                  >
                    <StyledDateInput
                      aria-label="finish date"
                      type="datetime-local"
                      min={`${DateTime.now()
                        .plus({ minutes: 5 })
                        .toFormat('yyyy-MM-dd')}T${DateTime.now().toFormat(
                        'T',
                      )}`}
                      value={
                        context.end_time
                          ? context.end_time.substring(0, 16)
                          : ''
                      }
                      onChange={onTimeChange}
                    />
                  </FormControl>
                </Box>

                <Box
                  mt="2"
                  px="3"
                  display="flex"
                  alignItems="center !important"
                  border="1px solid"
                  borderColor="mediumgrey"
                  borderRadius="8px"
                >
                  <FormControl
                    m="0"
                    mt="2"
                    required
                    name="isDiving"
                    label="Will anyone be diving?"
                  >
                    &nbsp;
                  </FormControl>
                  <CheckboxInput
                    onChange={handleSwitchIsDiving}
                    checked={!!isDiving}
                  />
                </Box>

                {/* People Aboard */}

                <FormControl label="People on board">
                  <Box display="flex" alignItems="center">
                    <Icon.Circle
                      bg="transparent"
                      border={`1px solid ${theme.colors.primary}`}
                      onClick={onMinusIconClick}
                    >
                      <MinusIcon color={theme.colors.primary} size={18} />
                    </Icon.Circle>

                    <Box width="70px" mx={2}>
                      <Input
                        type="number"
                        min={0}
                        value={onboard}
                        textAlign="center"
                        onChange={handlePeopleOnBoardChange}
                        onFocus={() => showMenuBar(false)}
                        onBlur={showMenuBar}
                      />
                    </Box>

                    <Icon.Circle
                      bg="transparent"
                      border="1px solid #FF5C39"
                      onClick={onPlusIconClick}
                    >
                      <PlusIcon color="#FF5C39" size={18} />
                    </Icon.Circle>
                  </Box>
                </FormControl>
              </NavList>
              {logonWithWa && isInWa && (
                <Box fontSize="1">
                  By continuing, you agree to the{' '}
                  <AnchorButton href="https://dfes.wa.gov.au/LOLO-Additional-Terms">
                    Log On/Log Off
                  </AnchorButton>{' '}
                  Terms & sharing your info, including last known position, with
                  Marine Rescue WA, WA Police & emergency services.
                </Box>
              )}
            </Card.Body>
          </Card>

          <Button
            onClick={onComplete}
            disabled={isStepComplete() === false}
            wide
          >
            Next
          </Button>
        </form>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default CreateFloatPlanStep;
